import EcsAgent from '@/classes/contacts/EcsAgent';
import EcsBusinessProcessContact from '@/classes/contacts/EcsBusinessProcessContact';
import EcsChannelContact from '@/classes/contacts/EcsChannelContact';
import EcsSupervisor from '@/classes/contacts/EcsSupervisor';
import EcsEnterpriseContact from '@/classes/contacts/EcsEnterpriseContact';
import EcsExternalContact from '@/classes/contacts/EcsExternalContact';
import EcsCrmContact from '@/classes/contacts/EcsCrmContact';
import { AGENT_STATE } from '@/constants/agent/constants';

const sortByFavorites = (contactA, contactB) => {
  if (contactA.isFavorite && !contactB.isFavorite) {
    return -1;
  } else if (!contactA.isFavorite && contactB.isFavorite) {
    return 1;
  } else {
    return 0;
  }
};

const getAgents = state => state.AGENTS.map(agent => new EcsAgent(agent)).sort(sortByFavorites);

const getSupervisors = state => state.SUPERVISORS.map(agent => new EcsSupervisor(agent)).sort(sortByFavorites);

const getBusinessProcesses = state =>
  state.BUSINESS_PROCESSES.map(contact => new EcsBusinessProcessContact(contact)).sort(sortByFavorites);

const getChannels = state => state.CHANNELS.map(contact => new EcsChannelContact(contact)).sort(sortByFavorites);

const getEnterpriseContacts = state =>
  state.ENTERPRISE.map(contact => new EcsEnterpriseContact(contact)).sort(sortByFavorites);

const getExternalContacts = state =>
  state.EXTERNAL.map(contact => new EcsExternalContact(contact)).sort(sortByFavorites);

const getCrmContacts = state => state.CRM_CONTACTS.map(contact => new EcsCrmContact(contact));

const getCrmTemplate = state => (contactId, businessProcessId) => {
  return (
    (state.CRM_TEMPLATES || []).find(
      template => template.contactId === contactId && template.businessProcessId === businessProcessId
    ) || {}
  ).data;
};

const getCrmCustomersContacts = state => state.CUSTOMERS.map(contact => new EcsCrmContact(contact));

const getAgent = (state, getters) => agentId => {
  if (!agentId) {
    return null;
  }
  return getters.getAgents.find(agent => agent.id === agentId);
};

const getSupervisor = (state, getters) => supervisorId => {
  if (!supervisorId) {
    return null;
  }
  return getters.getSupervisors.find(supervisor => supervisor.id === supervisorId);
};

const getChannel = (state, getters) => contactId => {
  if (!contactId) {
    return null;
  }
  return getters.getChannels.find(contact => contact.id === contactId);
};

const getBusinessProcess = (state, getters) => contactId => {
  if (!contactId) {
    return null;
  }
  return getters.getBusinessProcesses.find(contact => contact.id === contactId);
};

const getEnterpriseContact = (state, getters) => contactId => {
  if (!contactId) {
    return null;
  }
  return getters.getEnterpriseContacts.find(contact => contact.id === contactId);
};

const getEnterpriseContactByContactUri = (state, getters) => contactUri => {
  if (!contactUri) {
    return null;
  }
  return getters.getEnterpriseContacts.find(contact => contact['HPBX User'] === contactUri);
};

const getExternalContact = (state, getters) => contactId => {
  if (!contactId) {
    return null;
  }
  return getters.getExternalContacts.find(contact => contact.id === contactId);
};

const getCrmContact = (state, getters) => contactId => {
  if (!contactId) {
    return null;
  }
  return getters.getCrmContacts.find(contact => contact.id === contactId);
};

const getContact = (state, getters) => contactId => {
  if (!contactId) {
    return null;
  }

  return (
    getters.getAgent(contactId) ||
    getters.getSupervisor(contactId) ||
    getters.getChannel(contactId) ||
    getters.getBusinessProcess(contactId) ||
    getters.getEnterpriseContact(contactId) ||
    getters.getExternalContact(contactId) ||
    getters.getCrmContact(contactId)
  );
};

const getContactV2 = (state, getters) => contactId => {
  return contactId ? getters.getAllContacts[contactId] : null;
};

const getAllContacts = (state, getters) => {
  const contacts = [
    ...getters.getAgents,
    ...getters.getSupervisors,
    ...getters.getChannels,
    ...getters.getBusinessProcesses,
    ...getters.getEnterpriseContacts,
    ...getters.getExternalContacts,
    ...getters.getCrmContacts,
    ...getters.getCrmCustomersContacts
  ];

  const contactsMap = {};
  contacts.forEach(contact => {
    contactsMap[contact.id] = contact;
  });

  return contactsMap;
};

const getAgentByEndPoint = (state, getters) => endPoint => {
  return getters.getAgents.find(agent => agent.endPoint === endPoint);
};

const getSupervisorByEndPoint = (state, getters) => endPoint => {
  return getters.getSupervisors.find(supervisor => supervisor.endPoint === endPoint);
};

const getChannelByEndPoint = (state, getters) => endPoint => {
  return getters.getChannels.find(contact => contact.endPoint === endPoint);
};

const getBusinessProcessByEndPoint = (state, getters) => endPoint => {
  return getters.getBusinessProcesses.find(contact => contact.endPoint === endPoint);
};

const getEnterpriseContactByEndPoint = (state, getters) => endPoint => {
  return getters.getEnterpriseContacts.find(contact => contact.endPoint === endPoint);
};

const getExternalContactByEndPoint = (state, getters) => endPoint => {
  return getters.getExternalContacts.find(contact => {
    const endPoints = [...contact.phones, ...contact.mobiles].map(phone => phone.value);
    return endPoints.includes(endPoint);
  });
};

const getCrmContactByEndPoint = (state, getters) => endPoint => {
  return getters.getCrmContacts.find(contact => contact.endPoint === endPoint);
};

const getCrmContactByPhone = (state, getters) => phone => {
  return getters.getCrmContacts.find(contact =>
    [contact.phone1, contact.phone2, contact.phone3, contact.phone4].includes(phone)
  );
};

const getContactByEndPoint = (state, getters) => endPoint => {
  return (
    getters.getAgentByEndPoint(endPoint) ||
    getters.getSupervisorByEndPoint(endPoint) ||
    getters.getChannelByEndPoint(endPoint) ||
    getters.getBusinessProcessByEndPoint(endPoint) ||
    getters.getEnterpriseContactByEndPoint(endPoint) ||
    getters.getExternalContactByEndPoint(endPoint) ||
    getters.getCrmContactByEndPoint(endPoint)
  );
};

const getInternalBusinessContacts = (state, getters) => {
  return [...getters.getAgents, ...getters.getSupervisors, ...getters.getBusinessProcesses, ...getters.getChannels];
};

const getInternalBusinessContact = (state, getters) => id => {
  return getters.getInternalBusinessContacts.find(contact => contact.id === id);
};

const getInternalBusinessContactByEndPoint = (state, getters) => endPoint => {
  return getters.getInternalBusinessContacts.find(contact => contact.endPoint === endPoint);
};

const getFavoriteContacts = state => {
  return structuredClone(state.FAVORITES);
};

const getAvailabilityState = state => contactId => {
  return state.STATES[contactId] || AGENT_STATE.offline;
};

const getAllStates = state => {
  return state.STATES;
};

export default {
  getAllContacts,
  getContactV2,
  getAgents,
  getSupervisors,
  getChannels,
  getBusinessProcesses,
  getEnterpriseContacts,
  getExternalContacts,
  getCrmContacts,
  getCrmTemplate,
  getCrmCustomersContacts,

  getAgent,
  getSupervisor,
  getChannel,
  getBusinessProcess,
  getEnterpriseContact,
  getEnterpriseContactByContactUri,
  getExternalContact,
  getCrmContact,
  getContact,

  getAgentByEndPoint,
  getSupervisorByEndPoint,
  getChannelByEndPoint,
  getBusinessProcessByEndPoint,
  getEnterpriseContactByEndPoint,
  getExternalContactByEndPoint,
  getCrmContactByEndPoint,
  getCrmContactByPhone,
  getContactByEndPoint,

  getInternalBusinessContacts,
  getInternalBusinessContact,
  getInternalBusinessContactByEndPoint,

  getFavoriteContacts,
  getAvailabilityState,

  getAllStates
};
